import type { ColorAdjust } from "@/rendering";

/**
 * Color adjustment (B5).
 *
 * opencut-wasm 0.2.10 ships only a gaussian-blur effect shader and is a pinned
 * published dependency, so color grading cannot ride a wasm effect pass. Instead
 * we draw the source frame onto an OffscreenCanvas with `ctx.filter` set to the
 * equivalent CSS filter and hand the filtered canvas to the compositor. This
 * runs identically in preview and export (same resolve path) and only for clips
 * that carry a non-neutral adjustment.
 */

export function isNeutralColorAdjust(adjust?: ColorAdjust): boolean {
	return (
		!adjust ||
		(adjust.brightness === 1 &&
			adjust.contrast === 1 &&
			adjust.saturation === 1 &&
			adjust.warmth === 0)
	);
}

export function buildCanvasFilter(adjust: ColorAdjust): string {
	const parts = [
		`brightness(${adjust.brightness})`,
		`contrast(${adjust.contrast})`,
		`saturate(${adjust.saturation})`,
	];
	// Warmth approximation within Canvas2D filter limits: warm = sepia cast,
	// cool = a slight hue shift (no true per-channel temperature available).
	if (adjust.warmth > 0) {
		parts.push(`sepia(${(Math.min(adjust.warmth, 100) / 100) * 0.5})`);
	} else if (adjust.warmth < 0) {
		parts.push(`hue-rotate(${(Math.max(adjust.warmth, -100) / 100) * 18}deg)`);
	}
	return parts.join(" ");
}

// One scratch canvas per node, reused across frames (nodes are stable within a
// render tree). Keyed weakly so rebuilt nodes drop their canvas for GC. Never
// shared across nodes: concurrent resolves (Promise.all) would race a shared one.
const scratchByOwner = new WeakMap<object, OffscreenCanvas>();

export function applyColorAdjust({
	owner,
	source,
	width,
	height,
	adjust,
}: {
	owner: object;
	source: CanvasImageSource;
	width: number;
	height: number;
	adjust?: ColorAdjust;
}): CanvasImageSource {
	if (isNeutralColorAdjust(adjust) || width <= 0 || height <= 0) {
		return source;
	}

	let canvas = scratchByOwner.get(owner);
	if (!canvas || canvas.width !== width || canvas.height !== height) {
		canvas = new OffscreenCanvas(width, height);
		scratchByOwner.set(owner, canvas);
	}

	const ctx = canvas.getContext("2d");
	if (!ctx) {
		console.warn("[color-adjust] 2D context unavailable; skipping adjustment");
		return source;
	}

	ctx.clearRect(0, 0, width, height);
	ctx.filter = buildCanvasFilter(adjust as ColorAdjust);
	ctx.drawImage(source, 0, 0, width, height);
	ctx.filter = "none";
	return canvas;
}
